import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";
import { formatApiError } from "../lib/api";
import { Phone, ChevronRight } from "lucide-react";

const COUNTRIES = [
  "Slovakia (Slovak Republic)", "Czech Republic", "Austria", "Germany",
  "Hungary", "Poland", "Croatia", "Romania", "Slovenia", "Italy", "France",
  "Netherlands", "Belgium", "Spain", "Portugal", "Switzerland",
  "United Kingdom", "Ukraine", "Serbia", "Bulgaria", "Greece",
];

export default function Login() {
  const { login, register } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    email: "", password: "", confirm: "",
    name: "", company: "",
    tax_id: "",
    street: "", city: "", postal_code: "", country: "Slovakia (Slovak Republic)",
    phone: "", fax: "",
    captcha: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (mode === "register") {
      if (form.password.length < 7) {
        setErr("Heslo musí mať aspoň 7 znakov.");
        return;
      }
      if (form.password !== form.confirm) {
        setErr("Heslá sa nezhodujú.");
        return;
      }
      if (form.captcha.trim() !== "4") {
        setErr("Nesprávna odpoveď na bezpečnostnú otázku. (2 × 2 = ?)");
        return;
      }
    }

    setLoading(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register({
            email: form.email,
            password: form.password,
            name: form.name,
            company: form.company,
            tax_id: form.tax_id,
            street: form.street,
            city: form.city,
            postal_code: form.postal_code,
            country: form.country,
            phone: form.phone,
            fax: form.fax,
          });
      navigate(u.role === "admin" ? "/admin" : "/catalog");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top utility bar */}
      <div className="topbar">
        <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Phone size={11} />
            <span>{t("topbar_phone_label", "VOLAJTE")}: {t("topbar_phone", "+421 900 000 000")}</span>
          </div>
          <div className="hidden md:block font-bold" style={{ color: "var(--signal)" }}>
            {t("topbar_notice", "CENY V CENNÍKU SÚ NETTO CENY, NEOBSAHUJÚ DPH.")}
          </div>
          <nav className="flex items-center gap-1 text-[11px] uppercase">
            <Link to="/" className="px-2 py-1 hover:text-[color:var(--brand)]">Domov</Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <span className="px-2 py-1 font-bold" style={{ color: "var(--brand)" }}>Prihlásenie</span>
          </nav>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3" data-testid="login-brand">
            <div
              className="w-14 h-14 flex items-center justify-center rounded shrink-0"
              style={{ background: "var(--text)", color: "#fff", fontWeight: 900, fontSize: 24, letterSpacing: "-0.04em" }}
            >
              {t("header_logo_initials", "PS")}
            </div>
            <div className="leading-tight">
              <div className="font-bold text-xl" style={{ color: "var(--text)" }}>{t("header_company_name", "PROPARTS.SK")}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>{t("header_company_subtitle", "OEM diely · Veľkoobchod")}</div>
            </div>
          </Link>

          <div className="hidden lg:block flex-1 text-center px-4">
            <div className="text-base font-bold uppercase tracking-wide" style={{ color: "var(--signal)" }}>
              {t("header_price_period", "CENY PLATNÉ 04/2026!")}
            </div>
            <div className="text-base font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--signal)" }}>
              {t("header_price_subtext", "PRE PRESNÉ CENY POŠLITE EMAIL,")}
            </div>
            <div className="text-base font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--signal)" }}>
              {t("header_price_subtext2", "ALEBO SA PRIHLÁSTE DO ÚČTU.")}
            </div>
          </div>

          <div className="hidden md:block shrink-0" />
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/">Domov</Link> / <span style={{ color: "var(--text)" }}>Prihlásenie</span>
        </div>
      </div>

      <main
        className="flex-1 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.78), rgba(245,245,245,0.92)), url("/login-bg.jpg?v=2")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="max-w-[900px] w-full mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-3 uppercase tracking-wide" style={{ color: "var(--text)" }}>
          {mode === "login" ? t("login_welcome_title", "Vitajte, prihláste sa prosím") : "Registrácia nového zákazníka"}
        </h1>
        <div className="border-b-2 mb-6" style={{ borderColor: "var(--brand)" }} />

        {mode === "login" ? (
          <>
            {/* LOGIN BLOCK */}
            <div className="card-elev mb-6">
              <div className="section-title">{t("login_returning_title", "Existujúci zákazník: Prihláste sa")}</div>
              <div className="p-6">
                <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
                  <Field label="E-mailová adresa" name="email" type="email" value={form.email} onChange={onChange} required testid="email-input" />
                  <Field label="Heslo" name="password" type="password" value={form.password} onChange={onChange} required testid="password-input" />

                  {err && <ErrorBox msg={err} />}

                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-center pt-2">
                    <span />
                    <div className="flex items-center justify-between gap-3">
                      <button type="submit" disabled={loading} className="btn-primary" data-testid="submit-button">
                        {loading ? "Moment..." : "Prihlásiť"}
                      </button>
                      <Link to="/forgot-password" className="text-xs hover:underline" style={{ color: "var(--brand)" }} data-testid="forgot-password-link">
                        Zabudnuté heslo?
                      </Link>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <div className="card-elev">
              <div className="section-title">{t("login_new_customer_title", "Nový zákazník? Zadajte fakturačné údaje")}</div>
              <div className="p-6">
                <p className="text-sm mb-4" style={{ color: "var(--text-mute)" }}>
                  {t("login_new_customer_body", "Vytvorte si účet PROPARTS.SK, s ktorým nakúpite rýchlejšie, môžete sledovať stav objednávok a prezerať staršie objednávky. Po registrácii vám náš vedúci predaja priradí cenovú kategóriu.")}
                </p>
                <button
                  type="button"
                  onClick={() => { setMode("register"); setErr(""); }}
                  className="btn-ghost"
                  data-testid="toggle-mode"
                >
                  Vytvoriť nový účet <ChevronRight size={12} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* REGISTER BLOCK */}
            <p className="text-sm mb-5" style={{ color: "var(--text-mute)" }}>
              <span style={{ color: "var(--out-stock)" }}>*</span> Polia označené hviezdičkou sú povinné.
            </p>

            <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
              {/* Address Details */}
              <div className="card-elev">
                <div className="section-title">Fakturačné údaje</div>
                <div className="p-6 space-y-4">
                  <Field label="Meno a Priezvisko" name="name" value={form.name} onChange={onChange} required testid="register-name-input" />
                  <Field label="Ulica, číslo domu" name="street" value={form.street} onChange={onChange} required testid="register-street-input" />
                  <Field label="Mesto" name="city" value={form.city} onChange={onChange} required testid="register-city-input" />
                  <Field label="PSČ" name="postal_code" value={form.postal_code} onChange={onChange} required testid="register-postal-input" />
                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-center">
                    <label className="text-sm font-bold">
                      Krajina: <span style={{ color: "var(--out-stock)" }}>*</span>
                    </label>
                    <select name="country" className="input" required value={form.country} onChange={onChange} data-testid="register-country-input">
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Company Details */}
              <div className="card-elev">
                <div className="section-title">Údaje firmy</div>
                <div className="p-6 space-y-4">
                  <Field label="Názov firmy / Servisu" name="company" value={form.company} onChange={onChange} testid="register-company-input" />
                  <Field
                    label="IČ DPH"
                    name="tax_id"
                    value={form.tax_id}
                    onChange={onChange}
                    placeholder="napr. SK1234567890"
                    testid="register-tax-id-input"
                  />
                </div>
              </div>

              {/* Additional Contact */}
              <div className="card-elev">
                <div className="section-title">Ďalšie kontakty</div>
                <div className="p-6 space-y-4">
                  <Field label="Telefón" name="phone" value={form.phone} onChange={onChange} required testid="register-phone-input" />
                  <Field label="Fax" name="fax" value={form.fax} onChange={onChange} testid="register-fax-input" />
                </div>
              </div>

              {/* Login Details */}
              <div className="card-elev">
                <div className="section-title">Prihlasovacie údaje</div>
                <div className="p-6 space-y-4">
                  <Field label="E-mailová adresa" name="email" type="email" value={form.email} onChange={onChange} required testid="email-input" />
                  <Field label="Heslo (min. 7 znakov)" name="password" type="password" value={form.password} onChange={onChange} required testid="password-input" />
                  <Field label="Potvrdenie hesla" name="confirm" type="password" value={form.confirm} onChange={onChange} required testid="register-confirm-input" />
                </div>
              </div>

              {/* Captcha */}
              <div className="card-elev">
                <div className="section-title">Bezpečnostná otázka</div>
                <div className="p-6 space-y-4">
                  <Field
                    label="Koľko je 2 × 2 = ?"
                    name="captcha"
                    value={form.captcha}
                    onChange={onChange}
                    required
                    testid="register-captcha-input"
                  />
                </div>
              </div>

              {err && <ErrorBox msg={err} />}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setMode("login"); setErr(""); }}
                  className="btn-ghost"
                  data-testid="toggle-mode"
                >
                  ← Späť na prihlásenie
                </button>
                <button type="submit" disabled={loading} className="btn-primary" data-testid="submit-button">
                  {loading ? "Moment..." : "Registrácia"} <ChevronRight size={14} />
                </button>
              </div>
            </form>
          </>
        )}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-10 border-t" style={{ background: "var(--surface-elev)", borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 text-xs flex flex-wrap items-center justify-between gap-3" style={{ color: "var(--text-mute)" }}>
          <div>{t("footer_text", "PROPARTS.SK — OEM Veľkoobchod dielov")} · © {new Date().getFullYear()}</div>
          <div>{t("footer_links", "Prihlásenie · Nákupný košík · Kontakt")}</div>
        </div>
      </footer>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div
      className="text-sm py-2 px-3"
      style={{ background: "#FEE", color: "var(--out-stock)", border: "1px solid #FCC", borderRadius: 4 }}
      data-testid="auth-error"
    >
      <strong>CHYBA:</strong> {msg}
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testid, placeholder }) {
  return (
    <div className="grid sm:grid-cols-[180px_1fr] gap-3 sm:items-center">
      <label className="text-sm font-bold">
        {label}: {required && <span style={{ color: "var(--out-stock)" }}>*</span>}
      </label>
      <input
        name={name}
        type={type}
        className="input"
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
